import { useContext, useState } from "react";

import { UserMenu } from "@stacklet/ui";
import { signOut as amplifySignOut } from "aws-amplify/auth";
import { graphql, useFragment } from "react-relay";

import { PreferencesDialog } from "app/components/dialogs/PreferencesDialog"; // avoiding barrel export to satisfy relay
import { SideBarContext } from "app/contexts/SideBarContext";
import { useUserWithIdentity } from "app/hooks";

import { AppVersion } from "./sidebar/AppVersion";

import type { NavBarUserMenu_query$key } from "./__generated__/NavBarUserMenu_query.graphql";

interface Props {
  queryRef: NavBarUserMenu_query$key | null;
}

export function NavBarUserMenu({ queryRef }: Props) {
  const [isPreferencesDialogOpen, setIsPreferencesDialogOpen] = useState(false);

  const { collapsed } = useContext(SideBarContext);
  const user = useUserWithIdentity();

  const data = useFragment(
    graphql`
      fragment NavBarUserMenu_query on Query {
        platform {
          ...AppVersion_version
        }
        whoami {
          displayName
        }
        ...PreferencesDialog_query
      }
    `,
    queryRef,
  );

  // Prefer the profile display name (editable on the profile page) over the
  // identity-provider attributes, falling back to email and raw identifiers.
  const username =
    data?.whoami.displayName ||
    user.name ||
    user.email ||
    user.identity?.userId ||
    user.username;

  const signOut = async () => {
    await amplifySignOut();
    window.location.reload();
  };

  return (
    <>
      <UserMenu
        collapsed={collapsed}
        onLogout={signOut}
        onPreferences={() => setIsPreferencesDialogOpen(true)}
        username={username ?? ""}
      >
        {data?.platform ? <AppVersion platform={data.platform} /> : null}
      </UserMenu>
      {data ? (
        <PreferencesDialog
          isDialogOpen={isPreferencesDialogOpen}
          openDialog={setIsPreferencesDialogOpen}
          queryRef={data}
        />
      ) : null}
    </>
  );
}
